import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Container,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Refresh,
  Home,
  TrendingUp,
  Timer,
  EmojiEvents,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

const QuizResults = () => {
  const { quizId, attemptId } = useParams();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [attemptId]);

  const loadResults = async () => {
    try {
      setLoading(true);
      
      const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api');
      const token = localStorage.getItem('accessToken');

      // Charger la tentative
      const attemptResponse = await axios.get(
        `${API_BASE_URL}/attempt/${attemptId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setAttempt(attemptResponse.data.attempt);
      setQuiz(attemptResponse.data.attempt.quiz);
      
      console.log('📊 Résultats chargés:', attemptResponse.data.attempt);
    } catch (error) {
      console.error('Erreur lors du chargement des résultats:', error);
      toast.error('Impossible de charger les résultats');
      navigate('/quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    navigate(`/quiz/${quizId}`);
  };

  const handleBackToQuizzes = () => {
    navigate('/quiz');
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!attempt || !quiz) {
    return (
      <Container>
        <Alert severity="error">Résultats non trouvés</Alert>
      </Container>
    );
  }

  const passed = attempt.passed;
  const canRetry = attempt.attemptNumber < quiz.maxAttempts;

  return (
    <Container maxWidth="md">
      {/* Header avec score */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 3, 
          textAlign: 'center',
          bgcolor: passed ? 'success.lighter' : 'error.lighter',
        }}
      >
        <Box sx={{ mb: 2 }}>
          {passed ? (
            <EmojiEvents sx={{ fontSize: 80, color: 'success.main' }} />
          ) : (
            <Cancel sx={{ fontSize: 80, color: 'error.main' }} />
          )}
        </Box>
        
        <Typography variant="h3" gutterBottom>
          {attempt.score}%
        </Typography>
        
        <Typography variant="h5" gutterBottom color={passed ? 'success.main' : 'error.main'}>
          {passed ? '🎉 Félicitations ! Quiz réussi !' : '😔 Quiz non réussi'}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          Score minimum requis : {quiz.passingScore}%
        </Typography>

        <Box sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
          <LinearProgress 
            variant="determinate" 
            value={attempt.score} 
            color={passed ? 'success' : 'error'}
            sx={{ height: 10, borderRadius: 1 }}
          />
        </Box>
      </Paper>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Points obtenus
              </Typography>
              <Typography variant="h6">
                {attempt.pointsEarned || 0}/{attempt.maxPoints || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Temps passé
              </Typography>
              <Typography variant="h6">
                {Math.round((new Date(attempt.completedAt) - new Date(attempt.createdAt)) / 60000)} min
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Tentative
              </Typography>
              <Typography variant="h6">
                {attempt.attemptNumber}/{quiz.maxAttempts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Statut
              </Typography>
              <Chip 
                label={passed ? 'Réussi' : 'Échoué'} 
                color={passed ? 'success' : 'error'}
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Corrections détaillées */}
      {quiz.showCorrectAnswers && attempt.results && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Corrections détaillées
          </Typography>

          {attempt.results.map((result, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip 
                  label={`Question ${index + 1}`} 
                  size="small" 
                  variant="outlined"
                />
                {result.isCorrect === true && (
                  <CheckCircle color="success" />
                )}
                {result.isCorrect === false && (
                  <Cancel color="error" />
                )}
                <Chip 
                  label={`${result.pointsEarned}/${result.maxPoints} pts`} 
                  size="small"
                  color={result.isCorrect ? 'success' : 'error'}
                />
              </Box>

              <Typography variant="body1" gutterBottom>
                {result.question}
              </Typography>

              <Box sx={{ ml: 2, mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Votre réponse : 
                  <Typography component="span" sx={{ ml: 1, fontWeight: 'bold' }}>
                    {Array.isArray(result.userAnswer) 
                      ? result.userAnswer.join(', ') 
                      : result.userAnswer || 'Non répondu'}
                  </Typography>
                </Typography>

                {quiz.showExplanations && quiz.questions[index]?.explanation && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {quiz.questions[index].explanation}
                    </Typography>
                  </Alert>
                )}
              </Box>

              {index < attempt.results.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 5 }}>
        <Button
          variant="outlined"
          startIcon={<Home />}
          onClick={handleBackToQuizzes}
        >
          Retour aux quiz
        </Button>
        {canRetry && !passed && (
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRetry}
          >
            Réessayer ({quiz.maxAttempts - attempt.attemptNumber} tentative{quiz.maxAttempts - attempt.attemptNumber > 1 ? 's' : ''} restante{quiz.maxAttempts - attempt.attemptNumber > 1 ? 's' : ''})
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default QuizResults;

